import React, {useRef} from 'react';
import './mpds-switcher.scss';

export type SwitcherOption = {
    value: string;
    label: string;
    dataAttribute?: string;
};

type Props = {
    options: SwitcherOption[];
    selectedValue: string;
    onChange: (value: string) => void;
    // Provide one of ariaLabel or ariaLabelledBy to name the group
    ariaLabel?: string;
    ariaLabelledBy?: string;
    id?: string;
    className?: string;
    'data-testid'?: string;
};

/**
 * MPDS-style switcher component
 * General-purpose switcher following the MPDS design pattern from roster-comparison.html
 *
 * Picks one value from a small set, so it uses radio group semantics: only the selected
 * option is in the tab order, and arrow keys move (and select) between options.
 *
 * @example
 * // Geography grain switcher
 * <MPDSSwitcher
 *   options={[
 *     { value: 'COUNTY', label: 'County', dataAttribute: 'county' },
 *     { value: 'ZIP', label: 'ZIP', dataAttribute: 'zip' }
 *   ]}
 *   selectedValue={grain}
 *   onChange={(value) => setGrain(value)}
 *   ariaLabel="Geography level"
 *   className="no-grain"
 * />
 */
const MPDSSwitcher = ({
    options,
    selectedValue,
    onChange,
    ariaLabel,
    ariaLabelledBy,
    id,
    className,
    'data-testid': testId,
}: Props) => {
    const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

    const select = (value: string) => {
        if (value !== selectedValue) {
            onChange(value);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
        const keyOffsets: Record<string, number> = {
            ArrowRight: 1,
            ArrowDown: 1,
            ArrowLeft: -1,
            ArrowUp: -1,
        };
        const offset = keyOffsets[event.key];
        if (offset === undefined) return;

        event.preventDefault();
        const nextIndex = (index + offset + options.length) % options.length;
        buttonRefs.current[nextIndex]?.focus();
        select(options[nextIndex].value);
    };

    // If nothing matches selectedValue, keep the first option reachable by keyboard
    const hasSelection = options.some((o) => o.value === selectedValue);

    return (
        <div
            className={`mpds-switcher ${className || ''}`}
            role='radiogroup'
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            id={id}
            data-testid={testId}
        >
            {options.map((option, index) => {
                const isSelected = selectedValue === option.value;
                return (
                    <button
                        key={option.value}
                        ref={(el) => {
                            buttonRefs.current[index] = el;
                        }}
                        className='mpds-switcher__btn'
                        role='radio'
                        aria-checked={isSelected}
                        tabIndex={isSelected || (!hasSelection && index === 0) ? 0 : -1}
                        type='button'
                        data-value={option.dataAttribute || option.value.toLowerCase()}
                        data-testid={testId ? `${testId}-${option.value.toLowerCase()}` : undefined}
                        onClick={() => select(option.value)}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
};

export default MPDSSwitcher;
